"use client";

import type { Control, FieldErrors } from "react-hook-form";
import type { CustomerServiceFormData } from "./customer-service-form.schema";
import type { OfficeSearchState } from "./get-office-search-state";

import { FormError } from "@homeinstead/ui/components/FormError";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@homeinstead/ui/components/Select";
import { AnimatePresence, motion } from "motion/react";
import { Controller } from "react-hook-form";

type FranchiseOption = {
  label: string;
  value: string;
};

type CustomerServiceFranchiseSelectProps = {
  control: Control<CustomerServiceFormData>;
  disabled?: boolean;
  errors: FieldErrors<CustomerServiceFormData>;
  franchiseOptions: FranchiseOption[];
  officeSearchState: OfficeSearchState;
};

function CustomerServiceFranchiseSelect({
  control,
  disabled,
  errors,
  franchiseOptions,
  officeSearchState,
}: CustomerServiceFranchiseSelectProps) {
  return (
    <AnimatePresence>
      {officeSearchState !== "idle" ? (
        <motion.div
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          initial={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.2, ease: "easeInOut" }}
        >
          {officeSearchState === "loading" ? (
            <p className="text-muted-foreground text-sm">Loading franchises…</p>
          ) : null}

          {officeSearchState === "error" ? (
            <FormError.p className="text-error">
              Failed to load franchises. Please try again.
            </FormError.p>
          ) : null}

          {officeSearchState === "no-offices" ? (
            <p className="text-error text-sm">
              No franchises found for this postal code.
            </p>
          ) : null}

          {officeSearchState === "has-offices" ? (
            <div>
              <Controller
                control={control}
                name="franchise"
                render={({ field }) => (
                  <Select
                    disabled={disabled}
                    name={field.name}
                    onValueChange={field.onChange}
                    value={field.value}
                  >
                    <SelectTrigger label="Location">
                      <SelectValue placeholder="Select a franchise" />
                    </SelectTrigger>
                    <SelectContent>
                      {franchiseOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.franchise ? (
                <FormError.span className="text-error">
                  {errors.franchise.message}
                </FormError.span>
              ) : null}
            </div>
          ) : null}
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

export { CustomerServiceFranchiseSelect };
